import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { Notice } from '@/components/admin/Notice';
import { SkeletonRows } from '@/components/admin/states';
import { claimSlot, fetchCandidateSlots } from '@/features/availability/api';
import { ApiError } from '@/lib/api/http';

/**
 * 受講生がサクセス面談の日程を候補日から選ぶ（D-183）.
 *
 * 事務局が日時未定で作成した面談（D-173）に対し、担当サクセスの実施可能日を
 * 一覧し、1つ選んで確定する。確定するとイベントの日時が入り、
 * この回のグループへ自動でお知らせが投稿される。
 */
export function MeetingSchedulePicker({ eventId }: { eventId: string }): ReactElement | null {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['candidate-slots', eventId],
    queryFn: () => fetchCandidateSlots(eventId),
  });
  const [selected, setSelected] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onClaim(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await claimSlot(eventId, selected);
      // 開催日時の表示（概要・一覧・ダッシュボード）をまとめて更新する
      await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['candidate-slots', eventId] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '確定に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mb-4 rounded-sm border border-wpnotice-warning bg-wpnotice-warning/10 px-4 py-3">
      <h2 className="mb-1 font-semibold text-wptext-strong">面談の日程を選んでください</h2>
      <p className="mb-3 text-wptext-muted">
        担当のサクセス担当が対応できる日時です。都合の良いものを1つ選んで確定してください。
        確定するとこの回のグループメッセージにお知らせが入ります。
        <strong>都合の合う日時が無い場合は、グループメッセージでご相談ください。</strong>
      </p>
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {query.isPending ? (
        <SkeletonRows rows={3} columns={2} />
      ) : query.isError ? (
        <p className="text-wpnotice-error">候補日を読み込めませんでした。</p>
      ) : query.data.length === 0 ? (
        <p className="text-wptext-muted">
          選べる候補日がまだ登録されていません。担当のサクセス担当が登録するとここに表示されます。
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            {query.data.map((slot) => (
              <label key={slot.id} className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="candidate-slot"
                  checked={selected === slot.id}
                  disabled={busy}
                  onChange={() => setSelected(slot.id)}
                />
                <span className="tabular-nums">
                  {new Date(slot.starts_at).toLocaleString('ja-JP', {
                    month: 'numeric',
                    day: 'numeric',
                    weekday: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  〜{' '}
                  {new Date(slot.ends_at).toLocaleTimeString('ja-JP', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </label>
            ))}
          </div>
          <button
            type="button"
            disabled={busy || !selected}
            onClick={() => void onClaim()}
            className="mt-3 rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {busy ? '確定中…' : 'この日時で確定する'}
          </button>
        </>
      )}
    </section>
  );
}
